import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useT, useLanguage } from "@/i18n/LanguageProvider";
import LanguageToggle from "./LanguageToggle";

const LOGO_EN = "/assets/brand/logo_eng.png";
const LOGO_AR = "/assets/brand/logo_Arabic.png";

export default function Navbar() {
  const t = useT();
  const { lang } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const logoSrc = lang === "ar" ? LOGO_AR : LOGO_EN;

  const navItems = [
    { to: "/", label: t.nav.home, key: "home" },
    { to: "/services", label: t.nav.services, key: "services" },
    { to: "/track", label: t.nav.track, key: "track" },
    { to: "/business-solutions", label: t.nav.solutions, key: "business-solutions" },
    { to: "/pricing", label: t.nav.pricing, key: "pricing" },
    { to: "/about", label: t.nav.about, key: "about" },
    { to: "/contact", label: t.nav.contact, key: "contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isHomeTop = location.pathname === "/" && !scrolled;
  const headerBg = isHomeTop
    ? "bg-transparent"
    : "bg-delfi-navy shadow-[0_4px_24px_-8px_rgba(20,27,77,0.4)]";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerBg}`}
      data-testid="site-navbar"
    >
      <div className="delfi-container flex items-center justify-between h-[76px]">
        <Link
          to="/"
          className="flex items-center gap-2 group"
          data-testid="navbar-logo-link"
          aria-label="Twasal Awasal"
        >
          <img
            src={logoSrc}
            alt="Twasal Awasal"
            className="h-11 md:h-12 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.03]"
            style={{ maxWidth: 170 }}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {navItems.map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `nav-link text-white/85 hover:text-white ${isActive ? "active text-white" : ""}`
              }
              data-testid={`nav-link-${item.key}`}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <LanguageToggle variant="dark" testId="navbar-lang-toggle" />
          <Link
            to="/login"
            className="text-white/85 hover:text-white text-sm font-medium"
            data-testid="navbar-login-link"
          >
            {t.nav.login}
          </Link>
          <Link to="/track" className="btn-delfi-primary text-sm" data-testid="navbar-cta-partner">
            {t.common.trackShipment}
          </Link>
        </div>

        <div className="flex lg:hidden items-center gap-3">
          <LanguageToggle variant="dark" testId="navbar-lang-toggle-mobile" />
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="text-white p-2"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            data-testid="navbar-mobile-toggle"
          >
            {mobileOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-delfi-dark border-t border-white/10" data-testid="mobile-nav-drawer">
          <nav className="delfi-container py-6 flex flex-col gap-4" aria-label="Mobile">
            {navItems.map((item) => (
              <NavLink
                key={item.key}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `text-lg font-medium ${isActive ? "text-delfi-red" : "text-white/90"}`
                }
                data-testid={`mobile-nav-link-${item.key}`}
              >
                {item.label}
              </NavLink>
            ))}
            <div className="flex flex-col gap-3 pt-4 border-t border-white/10">
              <Link to="/login" className="text-white/80" data-testid="mobile-login-link">
                {t.nav.login}
              </Link>
              <Link to="/partner" className="btn-delfi-primary justify-center" data-testid="mobile-cta-partner">
                {t.common.partnerWithUs}
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
